// UltraCDN — E0 console shell routing.
//
// Mirrors how BeDrive registers `driveRoutes` / the admin settings tree:
//  - a parent route whose `lazy` element is the layout (`cdn-layout.tsx`,
//    exporting `Component`), with one child per sub-page.
//  - each child gated by its own `cdn.*` permission via the existing
//    `authGuard` loader (same seam used across the app router).
//
// `cdnRoutes` is spread into `app-router.tsx` next to `driveRoutes`.
// `cdnAdminRoutes` is spread into the existing `adminRoutes` parent so the
// admin surfaces inherit the admin dashboard chrome + `admin.access` guard.
//
// E4–E14 do not edit this file's structure — they only replace a page
// `Component` import target, which already resolves to a routed, gated slot.

import {authGuard} from '@common/auth/guards/auth-route';
import {RouteObject} from 'react-router';

export const cdnRoutes: RouteObject[] = [
  {
    path: 'cdn',
    // Layout-level guard: no `cdn.access` → 404 (or /pricing when billing is on)
    // via the existing authGuard, same as any other unauthorised route.
    loader: () => authGuard({permission: 'cdn.access'}),
    lazy: () => import('@app/cdn/cdn-layout'),
    children: [
      {
        index: true,
        lazy: () => import('@app/cdn/pages/cdn-overview-page'),
      },
      {
        // UltraCDN (E1) — read-only audit log, gated by the baseline cdn.access.
        path: 'audit',
        loader: () => authGuard({permission: 'cdn.access'}),
        lazy: () => import('@app/cdn/pages/cdn-audit-log-page'),
      },
      {
        path: 'analytics',
        loader: () => authGuard({permission: 'cdn.analytics.view'}),
        lazy: () => import('@app/cdn/pages/cdn-analytics-page'),
      },
      {
        path: 'caching',
        loader: () => authGuard({permission: 'cdn.edge.manage'}),
        lazy: () => import('@app/cdn/pages/cdn-caching-page'),
      },
      {
        path: 'security',
        loader: () => authGuard({permission: 'cdn.edge.manage'}),
        lazy: () => import('@app/cdn/pages/cdn-security-page'),
      },
      {
        path: 'signed-urls',
        loader: () => authGuard({permission: 'cdn.signed_urls.manage'}),
        lazy: () => import('@app/cdn/pages/cdn-signed-urls-page'),
      },
      {
        path: 'keys',
        loader: () => authGuard({permission: 'cdn.keys.manage'}),
        lazy: () => import('@app/cdn/pages/cdn-keys-page'),
      },
      {
        path: 'domains',
        loader: () => authGuard({permission: 'cdn.domains.manage'}),
        lazy: () => import('@app/cdn/pages/cdn-domains-page'),
      },
      {
        path: 'images',
        loader: () => authGuard({permission: 'cdn.images.manage'}),
        lazy: () => import('@app/cdn/pages/cdn-images-page'),
      },
      {
        path: 'lifecycle',
        loader: () => authGuard({permission: 'cdn.files.manage'}),
        lazy: () => import('@app/cdn/pages/cdn-lifecycle-page'),
      },
      {
        path: 'billing',
        loader: () => authGuard({permission: 'cdn.billing.view'}),
        lazy: () => import('@app/cdn/pages/cdn-billing-page'),
      },
      {
        path: 'api',
        loader: () => authGuard({permission: 'cdn.api.manage'}),
        lazy: () => import('@app/cdn/pages/cdn-api-page'),
      },
      {
        // Internal design reference (admin-only) — gallery of every UI
        // primitive with its internal name. Gated like the admin CDN surfaces.
        path: 'style-guide',
        loader: () => authGuard({permission: 'cdn.admin.plans'}),
        lazy: () => import('@app/cdn/pages/cdn-style-guide-page'),
      },
    ],
  },
];

// Admin CDN surfaces — mounted under the existing `admin` parent route, so they
// reuse the admin dashboard chrome and the `admin.access` guard. Each adds its
// `cdn.admin.*` permission guard on top.
export const cdnAdminRoutes: RouteObject[] = [
  {
    path: 'cdn/tenants',
    loader: () => authGuard({permission: 'cdn.admin.tenants'}),
    lazy: () => import('@app/cdn/pages/cdn-admin-tenants-page'),
  },
  {
    path: 'cdn/plans',
    loader: () => authGuard({permission: 'cdn.admin.plans'}),
    lazy: () => import('@app/cdn/pages/cdn-admin-plans-page'),
  },
];
